import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { createHTTPError, ErrorBoundary, HTTPError } from '@gitbook/util-debug';
import { assertIsDefined } from '@gitbook/util-ts';

import { useContentReference } from '../../ContentReferences';
import { useMediaGallery } from '../../MediaGallery';
import { ContentEditorContext } from '../contexts';
import { DrawingGenericBlankslate } from './DrawingGenericBlankslate';
import { createDrawingFile, createDrawingSVG, useDrawingSVGElement } from './data';
import { GenerateDrawingToolbar } from './GenerateDrawingToolbar';

export type DrawingStaticViewRef = {
    /** Open the drawing into a zoomed view in the gallery */
    openInGallery: () => void;
};

type DrawingContentRef = api.ContentRefURL | api.ContentRefFile;

const DrawingSVG = React.forwardRef(function DrawingSVG(
    props: { contentRef: DrawingContentRef },
    ref: React.ForwardedRef<DrawingStaticViewRef>
) {
    const resolved = useContentReference(props.contentRef);
    if (!resolved.href) {
        throw new createHTTPError.NotFound(
            'Drawing file not found, the file may have been deleted'
        );
    }
    const gallery = useMediaGallery();
    React.useImperativeHandle(ref, () => ({
        openInGallery: () => {
            gallery.open([{ url: resolved.href }]);
        },
    }));
    const svg = useDrawingSVGElement(resolved.href);
    const svgHTML = svg?.outerHTML ?? null;
    assertIsDefined(svgHTML);
    return (
        <div
            // biome-ignore lint/security/noDangerouslySetInnerHtml: This is a trusted source
            dangerouslySetInnerHTML={{ __html: svgHTML }}
        />
    );
});

function DrawingError({ error }: { error: unknown }) {
    const message =
        error instanceof HTTPError && error.expose
            ? error.message
            : 'An error occurred while rendering this drawing file.';
    return <DrawingGenericBlankslate kind="danger" icon={Icon.Warning} message={message} />;
}

function EmptyReadOnlyDrawing() {
    return <DrawingGenericBlankslate icon={Icon.EditDrawing} message="Drawing file not found" />;
}

function EmptyEditableDrawing(props: { nodeKey: string; onSave: (file: File) => Promise<void> }) {
    const {
        blocks: { withDrawings },
    } = React.useContext(ContentEditorContext);
    assertIsDefined(withDrawings);
    return (
        <DrawingGenericBlankslate
            icon={Icon.EditDrawing}
            message="Double click to start sketching out your thoughts."
        >
            <GenerateDrawingToolbar
                nodeKey={props.nodeKey}
                maxSuggestions={2}
                analyticsFrom="empty"
                onSubmitDrawing={async (data) => {
                    return props.onSave(createDrawingFile(await createDrawingSVG(data)));
                }}
            />
        </DrawingGenericBlankslate>
    );
}

/**
 * SVG static rendering of the drawing file to be rendered in the document.
 */
export const DrawingStaticView = React.forwardRef(function DrawingStaticViewWithRef(
    props: {
        nodeKey: string;
        readOnly: boolean;
        contentRef: DrawingContentRef | null;
        fallbackContentRef: api.ContentRefURL | null;
        onSave: (file: File) => Promise<void>;
    },
    ref: React.ForwardedRef<DrawingStaticViewRef>
) {
    const { nodeKey, readOnly, contentRef, fallbackContentRef, onSave } = props;
    return (
        <div className="flex flex-col">
            <ErrorBoundary fallback={({ error }) => <DrawingError error={error} />}>
                {contentRef ? (
                    fallbackContentRef ? (
                        <React.Suspense
                            fallback={<DrawingSVG ref={ref} contentRef={fallbackContentRef} />}
                        >
                            <DrawingSVG ref={ref} contentRef={contentRef} />
                        </React.Suspense>
                    ) : (
                        <DrawingSVG ref={ref} contentRef={contentRef} />
                    )
                ) : readOnly ? (
                    <EmptyReadOnlyDrawing />
                ) : (
                    <EmptyEditableDrawing nodeKey={nodeKey} onSave={onSave} />
                )}
            </ErrorBoundary>
        </div>
    );
});
